import { useContext } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiGet, apiPost, apiPatch, apiDelete } from 'api'
import AuthContext from 'context/auth-context'

const getAdminDatumUsers = async (token, municipalityId) => {
  const url = `admin/municipalities/${municipalityId}/datum_users`
  const data = await apiGet({ url, token })
  return data
}

const createAdminDatumUser = async (token, municipalityId, params) => {
  const url = `admin/municipalities/${municipalityId}/datum_users`
  const data = await apiPost({ url, token, objectName: 'datum_user', params })
  return data
}

const updateAdminDatumUser = async (token, municipalityId, userId, params) => {
  const url = `admin/municipalities/${municipalityId}/datum_users/${userId}`
  const data = await apiPatch({ url, token, objectName: 'datum_user', params })
  return data
}

const deleteAdminDatumUser = async (token, municipalityId, userId) => {
  const url = `admin/municipalities/${municipalityId}/datum_users/${userId}`
  const data = await apiDelete({ url, token })
  return data
}

const getAdminDatumUserReports = async (token, municipalityId, userId) => {
  const url = `admin/municipalities/${municipalityId}/datum_users/${userId}/reports`
  const data = await apiGet({ url, token })
  return data
}

const updateAdminDatumUserReports = async (token, municipalityId, userId, params) => {
  const url = `admin/municipalities/${municipalityId}/datum_users/${userId}/reports`
  const data = await apiPatch({ url, token, objectName: 'datum_user_report', params })
  return data
}

export function useAdminDatumUsers({ municipalityId, options } = {}) {
  const token = useContext(AuthContext)
  const queryFn = () => getAdminDatumUsers(token, municipalityId)
  const queryKey = ['admin-datum-users', municipalityId]
  return useQuery(queryKey, queryFn, { enabled: !!municipalityId, ...options })
}

export function useCreateAdminDatumUser({ municipalityId, options } = {}) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    (params) => createAdminDatumUser(token, municipalityId, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries(['admin-datum-users', municipalityId])
        if (options?.onSuccess) {
          options.onSuccess(data)
        }
      }
    }
  )
}

export function useUpdateAdminDatumUser({ municipalityId, options } = {}) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    ({ userId, params }) => updateAdminDatumUser(token, municipalityId, userId, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries(['admin-datum-users', municipalityId])
        if (options?.onSuccess) {
          options.onSuccess(data)
        }
      }
    }
  )
}

export function useDeleteAdminDatumUser({ municipalityId, options } = {}) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    (userId) => deleteAdminDatumUser(token, municipalityId, userId),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries(['admin-datum-users', municipalityId])
        if (options?.onSuccess) {
          options.onSuccess(data)
        }
      }
    }
  )
}

export function useAdminDatumUserReports({ municipalityId, userId, options } = {}) {
  const token = useContext(AuthContext)
  const queryFn = () => getAdminDatumUserReports(token, municipalityId, userId)
  const queryKey = ['admin-datum-user-reports', municipalityId, userId]
  return useQuery(queryKey, queryFn, { enabled: !!municipalityId && !!userId, ...options })
}

export function useUpdateAdminDatumUserReports({ municipalityId, userId, options } = {}) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    (params) => updateAdminDatumUserReports(token, municipalityId, userId, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries(['admin-datum-user-reports', municipalityId, userId])
        if (options?.onSuccess) {
          options.onSuccess(data)
        }
      }
    }
  )
}
